import ThemedSelect from "../shared/ThemedSelect.jsx";
import { useEffect, useRef, useState } from "react";
import DropZone from "../shared/DropZone.jsx";
import "../../styles/dropzone.css";
import { Badge, Button, Col, Form, Row, Table } from "react-bootstrap";
import {
  FaPencilAlt,
  FaTrash,
  FaClipboardCheck,
  FaCommentDots,
  FaThumbsUp,
  FaEyeSlash,
  FaBell,
  FaExclamationTriangle,
  FaPlusSquare,
  FaExclamationCircle,
  FaCheckCircle,
  FaUserCheck,
  FaRedo,
  FaPhone,
  FaPhoneSquare,
  FaLock,
  FaPaperclip,
  FaFileAlt,
  FaImage,
  FaTimes,
} from "react-icons/fa";
import {
  listProgressFlags,
  listProgress,
  createProgress,
  updateProgress,
  deleteProgressEntry,
  attachProgressFile,
  removeProgressFile,
  getProgressContext,
  getProgressToFromOptions,
  getCurrentUser,
  fileContentUrl,
} from "../../services/apiClient.js";
import { formatDateTime } from "../../utils/date.js";
import "../../styles/progress.css";
import DropdownText from "./../shared/DropdownText.jsx";
import TabHeader from "./TabHeader.jsx";
import ClaimOotReasons from "./ClaimOotReasons.jsx";
import ClaimClientContact from "./ClaimClientContact.jsx";

// Modern stand-ins for the legacy custom icons, with WebForms-style colour coding:
// green = positive/update, yellow = attention, red = negative, blue = info.
const FLAG_STYLE = {
  daily_review: { icon: FaClipboardCheck, bg: "info", dark: true },
  adverse_feedback: { icon: FaCommentDots, bg: "danger" },
  positive_feedback: { icon: FaThumbsUp, bg: "success" },
  hide: { icon: FaEyeSlash, bg: "warning", dark: true },
  reminder: { icon: FaBell, bg: "warning", dark: true },
  important: { icon: FaExclamationTriangle, bg: "warning", dark: true },
  additional_quote: { icon: FaPlusSquare, bg: "info", dark: true },
  problem_job_review: { icon: FaExclamationCircle, bg: "danger" },
  client_update: { icon: FaCheckCircle, bg: "success" },
  assessor_update: { icon: FaUserCheck, bg: "success" },
  quote_follow_up: { icon: FaRedo, bg: "info", dark: true },
  closure_call: { icon: FaPhone, bg: "success" },
  // Same phone, different weight: this one is the client signing the work off,
  // and it is what the invoice's completion block prints (invoices.md).
  closure_call_client_signoff: { icon: FaPhoneSquare, bg: "success" },
  locked: { icon: FaLock, bg: "warning", dark: true },
};
const DEFAULT_STYLE = { icon: FaClipboardCheck, bg: "secondary" };

// Row colour by dominant flag — the legacy GetClass PRIORITY order, first match
// wins (ProgressPage.aspx.cs:1619).
//
// These name TEXT colours, not row backgrounds (Karen Renall, 2026-08-01). 1.0
// coloured the words and left the row white; 2.0 had been tinting the whole row
// with Bootstrap's contextual classes, which turns a busy claim into bands of
// pink and green and buries the note everyone is actually here to read. Colours
// and the mapping live in styles/progress.css.
const ROW_PRIORITY = [
  ["adverse_feedback", "complaint"],
  ["reminder", "reminder"],
  ["hide", "hidden"],
  ["important", "important"],
  ["problem_job_review", "important"], // 2.0 flag, reads with Important in 1.0's order
  ["client_update", "update"],
  ["assessor_update", "update"],
  ["positive_feedback", "positive"],
  ["closure_call", "positive"], // 2.0 flag, reads with Positive
  ["closure_call_client_signoff", "positive"], // ditto — same family
  ["daily_review", "review"],
];
function rowClass(entry) {
  for (const [key, name] of ROW_PRIORITY) if (entry[key]) return `progress-row-${name}`;
  return null;
}

// Progress entries, WebForms-style (full 1-to-1 — see business-rules/progress.md):
// a Date | Details | Other Info table where a row expands in place for editing
// (checkbox flags, To/From pickers, SOW Confirmation, Invoice Debt Recovery,
// complaint resolution, one attached file). The "Show flag" filter narrows to a
// single flag so each lives in its own view.

function ClaimProgressTab({ claimId, onChanged }) {
  const [flags, setFlags] = useState([]);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [entries, setEntries] = useState([]);
  const [editingId, setEditingId] = useState(null); // null | "new" | entry id
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const [ctx, setCtx] = useState(null); // SOW lines, invoices, complaint counts
  const [toFromOptions, setToFromOptions] = useState([]);
  const [me, setMe] = useState(null);
  const [complaintsFirst, setComplaintsFirst] = useState(false);
  const attachInputRef = useRef(null);
  const attachTargetRef = useRef(null);
  // True while the picker was opened from the EDITOR, where there is no entry id
  // yet and the entry has to be saved before the file can be attached.
  const uploadPendingRef = useRef(false);

  function load(flag = filter) {
    listProgress(claimId, flag)
      .then(setEntries)
      .catch(() => setError("Unable to load progress entries."));
  }

  // One file per progress line (legacy GPSClaimProgress.Attachment).
  function startAttach(entryId) {
    attachTargetRef.current = entryId;
    attachInputRef.current?.click();
  }
  async function onAttachPick(e) {
    const file = e.target.files?.[0];
    const entryId = attachTargetRef.current;
    const pending = uploadPendingRef.current;
    uploadPendingRef.current = false;
    e.target.value = "";
    if (!file) return;
    // Picked from inside the editor: same save-then-attach path as a drop.
    if (pending) return saveThenAttach([file]);
    await sendAttachment(entryId, file);
  }
  // Dropping onto an entry takes the same path as the paperclip. ONE file per
  // line (legacy GPSClaimProgress.Attachment is a single reference), so a
  // multi-file drop takes the first and says so rather than silently losing the
  // rest — a drop that quietly discards four of five files is worse than a
  // refusal.
  async function sendAttachment(entryId, file, extraCount = 0) {
    if (!file || !entryId) return;
    try {
      await attachProgressFile(claimId, entryId, file);
      load();
      if (extraCount > 0) {
        setError(
          `Attached "${file.name}". A progress note holds one file, so the other ` +
            `${extraCount} were not attached — put those on the Files tab.`
        );
      }
    } catch (err) {
      setError(err.response?.data?.error || "Unable to attach file.");
    }
  }
  async function removeFile(entryId) {
    if (!window.confirm("Remove the attached file?")) return;
    try {
      await removeProgressFile(claimId, entryId);
      load();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to remove file.");
    }
  }

  function loadContext() {
    getProgressContext(claimId).then(setCtx).catch(() => {});
  }
  useEffect(() => {
    listProgressFlags().then(setFlags).catch(() => {});
    getProgressToFromOptions(claimId).then(setToFromOptions).catch(() => {});
    getCurrentUser().then(setMe).catch(() => {});
    loadContext();
    load("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId]);

  function blankForm() {
    const f = {
      details: "", other_info: "", to_party: "", from_party: "",
      scope_line_id: "", sow_confirmed: false,
      debt_recovery_invoice_id: "", external_approval: false,
      complaint_resolution_details: "", complaint_resolution_date: "",
    };
    flags.forEach((fl) => (f[fl.key] = false));
    return f;
  }
  function fromEntry(entry) {
    const f = {
      details: entry.details || "",
      other_info: entry.other_info || "",
      to_party: entry.to_party || "",
      from_party: entry.from_party || "",
      scope_line_id: entry.scope_line_id || "",
      sow_confirmed: !!entry.scope_line_confirmed,
      debt_recovery_invoice_id: entry.debt_recovery_invoice_id || "",
      external_approval: !!entry.debt_invoice_external_approval,
      complaint_resolution_details: entry.complaint_resolution_details || "",
      complaint_resolution_date: entry.complaint_resolution_date
        ? String(entry.complaint_resolution_date).slice(0, 10)
        : "",
    };
    flags.forEach((fl) => (f[fl.key] = !!entry[fl.key]));
    return f;
  }

  function changeFilter(event) {
    setFilter(event.target.value);
    load(event.target.value);
  }
  function handleText(event) {
    const { name, value } = event.target;
    setForm((c) => ({ ...c, [name]: value }));
  }
  function toggleFlag(key) {
    setForm((c) => ({ ...c, [key]: !c[key] }));
  }
  function startAdd() {
    setError("");
    setForm(blankForm());
    setEditingId("new");
  }
  function startEdit(entry) {
    setError("");
    setForm(fromEntry(entry));
    setEditingId(entry.id);
  }
  // Returns the entry's id so a caller can act on the row it just created —
  // dropping a file onto an UNSAVED entry saves it first and then attaches to
  // the id that comes back (see saveThenAttach).
  // `keepOpen` = this save happened FOR something else (a dropped file), not
  // because the user finished writing. Karen Sweatman, 2026-08-12: "don't close
  // until save is clicked by user" — dropping a file mid-sentence used to save
  // the note and shut the editor, so you lost your place in your own note.
  //
  // The editor must then re-point at the row that now EXISTS. Leaving it on
  // "new" would make the next Save create a second entry from the same text,
  // which is a far worse bug than the one being fixed.
  async function save({ keepOpen = false } = {}) {
    setError("");
    try {
      let savedId = editingId;
      if (editingId === "new") {
        const created = await createProgress(claimId, form);
        savedId = created?.id ?? null;
      } else {
        await updateProgress(claimId, editingId, form);
      }
      setEditingId(keepOpen && savedId ? savedId : null);
      load();
      loadContext();
      // A progress entry is what CLEARS the overdue chips (client update,
      // assessor update, daily review), so the header has to be re-read or the
      // job still looks overdue after you have just done it.
      onChanged?.();
      return savedId;
    } catch (err) {
      setError(err.response?.data?.error || "Unable to save entry.");
      return null;
    }
  }

  // A file dropped on an entry that does not exist yet. 1.0 lets you attach while
  // creating the line; 2.0 needed the entry saved first because the attachment
  // hangs off its id. Rather than make the user do that two-step by hand, the
  // drop DOES it: save, take the new id, attach to it (Robert, 2026-08-06).
  //
  // If the save is refused the file is not attached and the save error stands —
  // silently dropping the file would be the worst outcome, so it is named.
  async function saveThenAttach(files) {
    const id = await save({ keepOpen: true });
    if (!id) {
      setError((prev) => prev || "The entry could not be saved, so the file was not attached.");
      return;
    }
    await sendAttachment(id, files[0], files.length - 1);
  }

  async function removeEntry(entry) {
    if (!window.confirm("Are you sure you want to delete this progress line?")) return;
    setError("");
    try {
      await deleteProgressEntry(claimId, entry.id);
      load();
      loadContext();
      onChanged?.();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to delete entry.");
    }
  }

  function editRow(key, dateLabel) {
    return (
      <tr key={key}>
        <td className="text-nowrap">{dateLabel}</td>
        <td className="align-top">
          <Form.Control
            as="textarea"
            size="sm"
            className="progress-edit-details"
            name="details"
            value={form.details}
            onChange={handleText}
          />
        </td>
        <td>
          <Row className="g-1 mb-2">
            {flags.map((fl) => {
              const st = FLAG_STYLE[fl.key] || DEFAULT_STYLE;
              const Icon = st.icon;
              return (
                <Col xs={6} key={fl.key}>
                  <Form.Check
                    type="checkbox"
                    id={`pf-${fl.key}`}
                    label={<><Icon className={`me-1 text-${st.bg}`} />{fl.label}</>}
                    checked={!!form[fl.key]}
                    onChange={() => toggleFlag(fl.key)}
                  />
                </Col>
              );
            })}
          </Row>
          <Form.Control size="sm" className="mb-1" placeholder="Other Info" name="other_info" value={form.other_info} onChange={handleText} />
          {/* To/From: the legacy DropdownTextbox — pick from the grouped dropdown
              to FILL the text field, then edit freely; the text is what saves. */}
          <DropdownText name="to_party" value={form.to_party} onChange={handleText} groups={toFromOptions} placeholder="To" className="mb-1" />
          <DropdownText name="from_party" value={form.from_party} onChange={handleText} groups={toFromOptions} placeholder="From" className="mb-1" />

          {/* SOW Confirmation (legacy ScopeOfWorksLine): pick a scope line flagged
              needs-confirmation; the Confirmed tick writes back to the line. */}
          {(ctx?.sow_lines?.length || 0) > 0 ? (
            <div className="d-flex align-items-center gap-2 mb-1">
              <ThemedSelect size="sm" name="scope_line_id" value={form.scope_line_id} onChange={handleText} data-hint="SOW Confirmation">
                <option value="">SOW Confirmation…</option>
                {ctx.sow_lines.map((l) => (
                  <option key={l.id} value={l.id}>
                    Line {l.line_no}: {l.confirmed ? "Confirmed - " : ""}{(l.description || "").slice(0, 60)}
                  </option>
                ))}
              </ThemedSelect>
              {form.scope_line_id ? (
                <Form.Check
                  type="checkbox"
                  id={`sow-conf-${key}`}
                  className="text-nowrap"
                  label="Confirmed"
                  checked={!!form.sow_confirmed}
                  onChange={() => setForm((c) => ({ ...c, sow_confirmed: !c.sow_confirmed }))}
                />
              ) : null}
            </div>
          ) : null}

          {/* Invoice Debt Recovery (legacy InvoiceDebtRecovery): only offered when
              the claim has invoices; External Approval stamps the invoice. */}
          {(ctx?.invoices?.length || 0) > 0 ? (
            <div className="d-flex align-items-center gap-2 mb-1">
              <ThemedSelect size="sm" name="debt_recovery_invoice_id" value={form.debt_recovery_invoice_id} onChange={handleText} data-hint="Invoice Debt Recovery">
                <option value="">Invoice Debt Recovery…</option>
                {ctx.invoices.map((i) => (
                  <option key={i.id} value={i.id}>
                    #{i.invoice_no} - ${Number(i.total).toFixed(2)} - {i.has_been_paid ? "Paid" : "Not Paid"}
                  </option>
                ))}
              </ThemedSelect>
              {form.debt_recovery_invoice_id ? (
                <Form.Check
                  type="checkbox"
                  id={`ext-appr-${key}`}
                  className="text-nowrap"
                  label="External Approval"
                  checked={!!form.external_approval}
                  onChange={() => setForm((c) => ({ ...c, external_approval: !c.external_approval }))}
                />
              ) : null}
            </div>
          ) : null}

          {/* Complaint resolution (legacy): shown while Adverse Feedback is ticked. */}
          {form.adverse_feedback ? (
            <div className="border rounded p-2 mb-1">
              <div className="small fw-semibold mb-1">Complaint resolution</div>
              <Form.Control
                size="sm"
                className="mb-1"
                as="textarea"
                rows={2}
                placeholder="Resolution details"
                name="complaint_resolution_details"
                value={form.complaint_resolution_details}
                onChange={handleText}
              />
              <Form.Control
                size="sm"
                type="date"
                name="complaint_resolution_date"
                value={form.complaint_resolution_date}
                onChange={handleText}
                data-hint="Resolution date"
                style={{ width: 170 }}
              />
            </div>
          ) : null}
        </td>
        {/* An entry being written can still take a file: drop one here and the
            entry is saved first so the attachment has a row to hang off. This is
            what 1.0 offers by having the uploader on the create form itself. */}
        <td className="text-center align-middle small">
          <DropZone
            label="Drop a file — the entry is saved first"
            onFiles={saveThenAttach}
          >
            <Button
              variant="outline-secondary"
              size="sm"
              className="py-0 px-1"
              data-hint="Save this entry and attach a file, or drag one here" aria-label="Save this entry and attach a file, or drag one here"
              onClick={() => {
                uploadPendingRef.current = true;
                attachInputRef.current?.click();
              }}
            >
              <FaPaperclip />
            </Button>
          </DropZone>
        </td>
        <td className="text-end">
          <div className="d-grid gap-1">
            {/* Wrapped, not passed bare: onClick hands the click EVENT to the
                first argument, which is now an options object. */}
            <Button size="sm" onClick={() => save()}>Save</Button>
            <Button size="sm" variant="secondary" onClick={() => setEditingId(null)}>Cancel</Button>
          </div>
        </td>
      </tr>
    );
  }

  // One attached file per line: thumbnail (image) / icon (doc/no-bytes) + remove,
  // or an attach button when empty. Images open full size in a new tab.
  function fileCell(entry) {
    if (!entry.file_id) {
      return (
        // The empty cell is itself a drop target, so a file can be dragged
        // straight onto the line it belongs to (Mia, 2026-08-06).
        <DropZone
          label="Drop a file onto this note"
          onFiles={(files) => sendAttachment(entry.id, files[0], files.length - 1)}
        >
          <Button variant="outline-secondary" size="sm" className="py-0 px-1" data-hint="Attach a file, or drag one here" aria-label="Attach a file, or drag one here" onClick={() => startAttach(entry.id)}>
            <FaPaperclip />
          </Button>
        </DropZone>
      );
    }
    const previewId = entry.file_thumb_id || (entry.file_has_bytes ? entry.file_id : null);
    const viewHref = entry.file_has_bytes ? fileContentUrl(entry.file_id) : undefined;
    return (
      <div className="d-flex align-items-center justify-content-center gap-1">
        {entry.file_is_image && previewId ? (
          <a href={viewHref} target="_blank" rel="noreferrer" data-hint={entry.file_name}>
            <img src={fileContentUrl(previewId)} alt={entry.file_name} style={{ width: 44, height: 44, objectFit: "cover" }} className="rounded border" />
          </a>
        ) : (
          <a href={entry.file_has_bytes ? fileContentUrl(entry.file_id, { download: true }) : undefined} data-hint={entry.file_name} className="text-secondary">
            {entry.file_is_image ? <FaImage size={22} /> : <FaFileAlt size={22} />}
          </a>
        )}
        <Button variant="link" size="sm" className="p-0 text-danger" data-hint="Remove file" aria-label="Remove file" onClick={() => removeFile(entry.id)}>
          <FaTimes />
        </Button>
      </div>
    );
  }

  function viewRow(entry) {
    return (
      <tr key={entry.id} className={rowClass(entry) || undefined}>
        <td className="progress-entry-date text-nowrap">{formatDateTime(entry.created_at)}</td>
        <td className="progress-entry-details" style={{ whiteSpace: "pre-wrap" }}>{entry.details || "-"}</td>
        {/* Other Info sits a step down from Details on purpose (Karen Sweatman
            2026-08-12): it is the sidecar — flags, the note, the SOW reference —
            and at body size it competed with the note itself. `small` on the
            CELL so the badges inside scale with it, their font-size being em. */}
        <td className="progress-entry-meta small">
          <div className="mb-1">
            {flags
              .filter((fl) => entry[fl.key])
              .map((fl) => {
                const st = FLAG_STYLE[fl.key] || DEFAULT_STYLE;
                const Icon = st.icon;
                return (
                  <Badge
                    bg={st.bg}
                    text={st.dark ? "dark" : undefined}
                    className="me-1 mb-1"
                    key={fl.key}
                  >
                    <Icon className="me-1" />
                    {fl.label}
                  </Badge>
                );
              })}
          </div>
          {entry.other_info ? <div className="mb-1">{entry.other_info}</div> : null}
          {entry.scope_line_id ? (
            <div className="small">
              SOW Line {entry.scope_line_no}: {entry.scope_line_confirmed ? (
                <Badge bg="success">Confirmed</Badge>
              ) : (
                <Badge bg="warning" text="dark">Needs confirmation</Badge>
              )}{" "}
              <span className="text-muted">{(entry.scope_line_description || "").slice(0, 80)}</span>
            </div>
          ) : null}
          {entry.debt_recovery_invoice_id ? (
            <div className="small">
              Invoice Debt Recovery: #{entry.debt_invoice_no} ${Number(entry.debt_invoice_total).toFixed(2)}{" "}
              ({entry.debt_invoice_paid ? "Paid" : "Not Paid"}
              {entry.debt_invoice_external_approval ? ": Approved" : ""})
            </div>
          ) : null}
          {entry.adverse_feedback && entry.complaint_resolution_date ? (
            <div className="small text-success">
              Resolved {String(entry.complaint_resolution_date).slice(0, 10)}
              {entry.complaint_resolution_details ? ` — ${entry.complaint_resolution_details}` : ""}
            </div>
          ) : null}
          <div className="small text-muted">
            {entry.to_party ? <span className="me-2">To: {entry.to_party}</span> : null}
            {entry.from_party ? <span className="me-2">From: {entry.from_party}</span> : null}
            <span>By: {entry.created_by_name || "-"}</span>
          </div>
        </td>
        <td className="progress-entry-file text-center">{fileCell(entry)}</td>
        <td className="progress-entry-actions text-end text-nowrap">
          <Button variant="outline-primary" size="sm" data-hint="Edit" aria-label="Edit" onClick={() => startEdit(entry)}>
            <FaPencilAlt />
          </Button>
          {/* Legacy rule: locked entries are deletable by Administrators only. */}
          {!entry.locked || me?.is_admin ? (
            <Button variant="outline-danger" size="sm" className="ms-1" data-hint={entry.locked ? "Locked — deleting as admin" : "Delete"} aria-label={entry.locked ? "Locked — deleting as admin" : "Delete"} onClick={() => removeEntry(entry)}>
              <FaTrash />
            </Button>
          ) : null}
        </td>
      </tr>
    );
  }

  // Notes search filters the already-loaded (flag-filtered) entries by text — instant,
  // matches details / other info / to / from.
  const q = search.trim().toLowerCase();
  let visibleEntries = q
    ? entries.filter((e) =>
        [e.details, e.other_info, e.to_party, e.from_party]
          .some((v) => (v || "").toLowerCase().includes(q))
      )
    : entries;
  if (complaintsFirst) {
    visibleEntries = [...visibleEntries].sort(
      (a, b) => (b.adverse_feedback === true) - (a.adverse_feedback === true)
    );
  }

  // The entry being edited may not be in the visible list at all. Three ways:
  // the flag filter excludes it, the search box excludes it, or it was just
  // created by a dropped file and `load()` (fire-and-forget) has not returned
  // yet. The editor is bound to `editingId`, not to the list, so it must render
  // regardless — otherwise the drop-keeps-the-editor-open change could CLOSE the
  // very editor it exists to hold open, stranding the user's typing in state
  // with nothing on screen (found in review, 2026-08-12).
  const editingDetached =
    editingId !== null &&
    editingId !== "new" &&
    !visibleEntries.some((e) => e.id === editingId);
  // Prefer the real timestamp — a filtered-out row is still in `entries`, it is
  // only missing from the filtered view.
  const detachedEntry = editingDetached ? entries.find((e) => e.id === editingId) : null;

  return (
    <>
      <TabHeader
        title="Progress Notes"
        help="progress"
        actions={
          <>
            <ThemedSelect size="sm" value={filter} onChange={changeFilter} style={{ width: 180 }} data-hint="Filter by flag">
              <option value="">All entries</option>
              {flags.map((fl) => (
                <option key={fl.key} value={fl.key}>{fl.label}</option>
              ))}
            </ThemedSelect>
            <Form.Control
              size="sm"
              type="search"
              style={{ width: 240 }}
              placeholder="Find text in an entry…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-hint="Search notes"
            />
            {editingId === null ? (
              <Button size="sm" onClick={startAdd}>New Progress Entry</Button>
            ) : null}
          </>
        }
      />

      {/* Legacy banners: unconfirmed SOW notes + unresolved complaints. */}
      {(ctx?.sow_unconfirmed || 0) > 0 ? (
        <div className="alert alert-warning py-2 small">
          {ctx.sow_unconfirmed === 1
            ? "There is 1 Scope Of Work note that needs confirmation."
            : `There are ${ctx.sow_unconfirmed} Scope Of Work notes that need confirmation.`}{" "}
          Confirm them via a progress entry (SOW Confirmation) or on the Quote tab.
        </div>
      ) : null}
      {(ctx?.unresolved_complaints || 0) > 0 ? (
        <div className="alert alert-danger py-2 small d-flex justify-content-between align-items-center">
          <span>
            {ctx.unresolved_complaints === 1
              ? "There is 1 complaint that needs to be resolved."
              : `There are ${ctx.unresolved_complaints} complaints that need to be resolved.`}
          </span>
          <Form.Check
            type="checkbox"
            id="complaints-first"
            className="mb-0"
            label="Show complaints first"
            checked={complaintsFirst}
            onChange={(e) => setComplaintsFirst(e.target.checked)}
          />
        </div>
      ) : null}

      {error ? <div className="alert alert-danger">{error}</div> : null}

      {/* Guided Client-Contact entry (Karen 2026-07-25, job 76393) — stamps the
          First Contact / Go-Ahead date and drops the entry. Self-hides once both
          milestones are recorded. Refresh entries + context so it updates. */}
      <ClaimClientContact
        claimId={claimId}
        clientContact={ctx?.client_contact}
        onCreated={() => {
          load();
          loadContext();
        }}
      />

      {/* Out-Of-Target reasons for this claim (Karen 2026-07-25) — set/see the
          SLA reason here, same picker as the SLA report. Self-hides when the
          claim has no out-of-target steps. */}
      <ClaimOotReasons claimId={claimId} />

      <Table bordered hover responsive size="sm" className="mb-0 align-middle progress-table">
        <thead className="table-surface">
          <tr>
            {/* Text columns in `ch`, icon columns in px (architecture.md §10).
                Other Info used to carry width:38%, which tied it to the WINDOW
                rather than to its contents: on a full-screen 1080p — where most
                of the office works — it left a badge and a name marooned in a
                third of the table while Details, the column people actually
                read, was squeezed into a narrow ribbon (Karen Sweatman / Robert
                2026-08-12, compared at four widths). A `ch` width is tied to the
                text, so the column stays the size of what it holds and every
                extra pixel goes to Details. `ch` rather than px because the
                Bootswatch themes differ in character width by about a third,
                so a px column that fits in one theme clips in another. */}
            {/* 28ch is right for READING and wrong for EDITING, so it is only
                28ch while nothing is open (Robert 2026-08-14: "on view its
                perfect... edit looks bad"). The editor puts thirteen flags in
                the same cell in two columns, and the longest label — "Problem
                Job Review" — needs about 23ch on its own, so at 28ch every
                label in both columns wrapped and the edit row grew to roughly
                twice the height it needed.
                The width follows `editingId`, so Details gives up the room only
                while a row is actually open and takes it straight back on Save
                or Cancel. Still `ch`, for the reason above. */}
            <th style={{ width: "19ch" }}>Date</th>
            <th>Details</th>
            <th style={{ width: editingId === null ? "28ch" : "50ch" }}>Other Info</th>
            <th style={{ width: 80 }} className="text-center">File</th>
            <th style={{ width: 90 }} className="text-end">Actions</th>
          </tr>
        </thead>
        <tbody>
          {editingId === "new" ? editRow("new", "New") : null}
          {editingDetached
            ? editRow(editingId, detachedEntry ? formatDateTime(detachedEntry.created_at) : "New")
            : null}
          {visibleEntries.map((entry) =>
            editingId === entry.id
              ? editRow(entry.id, formatDateTime(entry.created_at))
              : viewRow(entry)
          )}
          {/* `editingDetached` counts as content: with a filter on and the
              editor open, the table would otherwise show the editor AND
              "No entries match your search" at the same time. */}
          {visibleEntries.length === 0 && editingId !== "new" && !editingDetached ? (
            <tr>
              <td colSpan={5} className="text-center text-muted">
                {entries.length === 0 ? "No progress entries." : "No entries match your search."}
              </td>
            </tr>
          ) : null}
        </tbody>
      </Table>
      <input type="file" hidden ref={attachInputRef} onChange={onAttachPick} />
    </>
  );
}

export default ClaimProgressTab;
